import React, { useEffect, useState } from "react";
import {
    Card,
    Form,
    Row,
    Col,
    Select,
    Input,
    Button,
    Typography,
    Space,
    message,
    Table,
    Modal,
    InputNumber,
    Descriptions,
    DatePicker
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import axiosInstance from "../../../../configs";
import { useHistory, useParams } from "react-router-dom";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;



export default function PemuatanForm() {
    const [form] = Form.useForm();
    const history = useHistory();
    const { id } = useParams(); // Get ID from URL

    // States
    const [outgoingCandidates, setOutgoingCandidates] = useState([]);
    const [selectedOutgoing, setSelectedOutgoing] = useState(null);
    const [picks, setPicks] = useState([]);

    // UI States
    const [loadingData, setLoadingData] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false); // True if user clicked "Edit"
    const [isViewMode, setIsViewMode] = useState(!!id); // True if viewing existing record

    // Confirmation
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);
    const [saving, setSaving] = useState(false);

    // Fetch Candidates (Only on Create or Edit to re-select? Actually on Edit we might stick to existing)
    useEffect(() => {
        if (!id) {
            // Only fetch candidates if creating new
            axiosInstance.get("/pemuatan/outgoing-candidates")
                .then(res => setOutgoingCandidates(res.data || []))
                .catch(err => console.error(err));
        }
    }, [id]);

    // Fetch Existing Data if ID present
    useEffect(() => {
        if (id) {
            setLoadingData(true);
            axiosInstance.get(`/pemuatan/${id}`)
                .then(res => {
                    const header = res.data.header;
                    const details = res.data.details;

                    // Set Form
                    form.setFieldsValue({
                        id_outgoing: header.id_outgoing,
                        nomor_pemuatan: header.nomor_pemuatan,
                        tanggal_pemuatan: dayjs(header.tanggal_pemuatan),
                        no_polisi: header.no_polisi,
                        driver: header.driver
                    });

                    // Set Selected Outgoing (Meta)
                    setSelectedOutgoing({
                        id_outgoing: header.id_outgoing,
                        delivery_number: header.delivery_number,
                        document_number: header.document_number,
                        nama_customer: "Loading...", // Can fetch if needed or ignore
                        // outgoing status might be completed now
                    });

                    // Set Picks (Existing Details)
                    const existingPicks = details.map(d => ({
                        id: d.id_pemuatan_detail,
                        id_item_stock: d.id_item_stock,
                        item_code: d.item_code,
                        lokasi_lot: d.location || "RETRIEVED",
                        nopen: d.nopen || "RETRIEVED",
                        qty_muat: d.qty_muat,
                        id_outgoing_detail: d.id_outgoing_detail // Preserves existing linkage
                    }));
                    setPicks(existingPicks);
                })
                .catch(err => {
                    console.error(err);
                    message.error("Gagal mengambil data pemuatan");
                })
                .finally(() => setLoadingData(false));
        }
    }, [id, form]);

    const handleOutgoingChange = async (val) => {
        const candidate = outgoingCandidates.find(c => c.id_outgoing === val);
        setSelectedOutgoing(candidate);

        if (candidate) {
            const dateStr = dayjs().format("YYYYMMDD");
            const nopenPart = candidate.document_number || candidate.delivery_number || "000";
            const autoNum = `SJM-${dateStr}-${nopenPart}`;

            form.setFieldsValue({
                nomor_pemuatan: autoNum,
                tanggal_pemuatan: candidate.delivery_date ? dayjs(candidate.delivery_date) : dayjs()
            });

            try {
                const res = await axiosInstance.get(`/outgoing/${val}/with-details`);
                const details = res.data.details || [];

                const newPicks = [];
                for (const d of details) {
                    try {
                        const stockRes = await axiosInstance.get("/item-stock", {
                            params: {
                                item_code: d.item_code,
                                nopen: d.nopen_asal,
                                lokasi_lot: d.lot,
                                limit: 1
                            }
                        });
                        const stocks = stockRes.data?.data || [];
                        const match = stocks[0];

                        if (match) {
                            newPicks.push({
                                id: match.id_item_stock + "_" + Date.now() + Math.random(),
                                id_item_stock: match.id_item_stock,
                                item_code: d.item_code,
                                lokasi_lot: match.lokasi_lot,
                                nopen: match.nopen,
                                qty_muat: d.total_kemasan,
                                id_outgoing_detail: d.id_outgoing_detail // Map from Outgoing Detail
                            });
                        } else {
                            newPicks.push({
                                id: "MISSING_" + d.id_outgoing_detail,
                                id_item_stock: null,
                                item_code: d.item_code,
                                lokasi_lot: d.lot || "TIDAK DITEMUKAN",
                                nopen: d.nopen_asal || "?",
                                qty_muat: d.total_kemasan,
                                error: "Stok tidak ditemukan!",
                                id_outgoing_detail: d.id_outgoing_detail // Still keep ref
                            });
                        }
                    } catch (e) {
                        // ignore
                    }
                }
                setPicks(newPicks);
            } catch (err) {
                message.error("Gagal mengambil detail outgoing");
            }
        }
    };

    const onFinish = async (values) => {
        if (!selectedOutgoing && !id) return message.error("Pilih Outgoing dulu");
        if (picks.length === 0) return message.error("Belum ada barang yang dimuat");

        const errors = picks.filter(p => !p.id_item_stock);
        if (errors.length > 0) {
            return message.error("Ada barang yang tidak ditemukan stoknya.");
        }

        setPendingValues(values);
        setConfirmVisible(true);
    };

    const handleConfirmSubmit = async () => {
        setSaving(true);
        const values = pendingValues;

        const payload = {
            header: {
                id_outgoing: selectedOutgoing?.id_outgoing, // might use old ID
                nomor_pemuatan: values.nomor_pemuatan,
                tanggal_pemuatan: values.tanggal_pemuatan ? dayjs(values.tanggal_pemuatan).format("YYYY-MM-DD") : null,
                no_polisi: values.no_polisi,
                driver: values.driver,
                created_by: 1
            },
            details: picks.map(p => ({
                id_item_stock: p.id_item_stock,
                qty_muat: p.qty_muat,
                id_outgoing_detail: p.id_outgoing_detail // Include in payload
            }))
        };

        try {
            if (id) {
                // UPDATE
                await axiosInstance.put(`/pemuatan/${id}`, payload);
                message.success("Pemuatan berhasil diperbarui");
            } else {
                // CREATE
                await axiosInstance.post("/pemuatan", payload);
                message.success("Pemuatan berhasil disimpan");
            }
            setConfirmVisible(false);
            history.push("/inventory/pemuatan");
        } catch (err) {
            console.error(err);
            message.error(err.response?.data?.error || "Gagal menyimpan pemuatan");
        } finally {
            setSaving(false);
        }
    };

    // View/Edit Controls
    const inputsDisabled = isViewMode && !isEditMode;

    return (
        <Card className="kt-portlet" bodyStyle={{ padding: 24 }} loading={loadingData}>
            <Row justify="space-between">
                <Title level={3}>{id ? "Detail Pemuatan" : "Rekam Pemuatan"}</Title>
                {isViewMode && !isEditMode && (
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => setIsEditMode(true)}
                    >
                        Edit Data
                    </Button>
                )}
            </Row>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item label="Pilih Dokumen Outgoing" name="id_outgoing" rules={[{ required: true }]}>
                            <Select
                                onChange={handleOutgoingChange}
                                placeholder="Pilih dokumen..."
                                disabled={!!id} // Cannot change outgoing doc on edit for now to keep it simple
                            >
                                {outgoingCandidates.map(c => (
                                    <Option key={c.id_outgoing} value={c.id_outgoing}>
                                        {c.document_number ? `[BC: ${c.document_number}] ` : ''}{c.delivery_number}
                                    </Option>
                                ))}
                                {/* If editing, push current option if not in list */}
                                {id && selectedOutgoing && (
                                    <Option key={selectedOutgoing.id_outgoing} value={selectedOutgoing.id_outgoing}>
                                        {selectedOutgoing.delivery_number}
                                    </Option>
                                )}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Nomor Pemuatan" name="nomor_pemuatan" rules={[{ required: true }]}>
                            <Input placeholder="Auto Generated" disabled={inputsDisabled} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Tanggal Pemuatan" name="tanggal_pemuatan" rules={[{ required: true }]}>
                            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" disabled={inputsDisabled} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="No Polisi" name="no_polisi">
                            <Input disabled={inputsDisabled} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Driver" name="driver">
                            <Input disabled={inputsDisabled} />
                        </Form.Item>
                    </Col>
                </Row>

                {(selectedOutgoing || picks.length > 0) && (
                    <div style={{ marginTop: 20 }}>
                        <Title level={4}>Rincian Pemuatan (Picks)</Title>
                        <Table
                            dataSource={picks}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            columns={[
                                { title: "Item", dataIndex: "item_code" },
                                { title: "Nopen Asal", dataIndex: "nopen" },
                                { title: "Lot / Lokasi", dataIndex: "lokasi_lot" },
                                {
                                    title: "Qty Muat",
                                    dataIndex: "qty_muat",
                                    render: (val, r) => r.error ? <Text type="danger">{r.error}</Text> : val
                                },
                            ]}
                        />
                    </div>
                )}

                <Row justify="end" style={{ marginTop: 20 }}>
                    <Space>
                        <Button onClick={() => history.push("/inventory/pemuatan")}>
                            {isViewMode && !isEditMode ? "Kembali" : "Batal"}
                        </Button>
                        {(!isViewMode || isEditMode) && (
                            <Button type="primary" htmlType="submit">
                                {id ? "Simpan Perubahan" : "Simpan Pemuatan"}
                            </Button>
                        )}
                    </Space>
                </Row>
            </Form>

            <Modal
                title={id ? "Konfirmasi Update Pemuatan" : "Konfirmasi Simpan Pemuatan"}
                open={confirmVisible}
                onOk={handleConfirmSubmit}
                onCancel={() => setConfirmVisible(false)}
                confirmLoading={saving}
                okText="Simpan"
                cancelText="Batal"
            >
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <p>
                        {id
                            ? "Anda yakin ingin mengubah data ini? Stok lama akan dikembalikan lalu dipotong ulang sesuai data baru."
                            : "Apakah Anda yakin data pemuatan sudah benar? Stok akan dikurangi sesuai rincian."}
                    </p>

                    {/* Summary Info */}
                    {pendingValues && (
                        <>
                            <Descriptions column={1} size="small" bordered style={{ marginTop: 10 }}>
                                <Descriptions.Item label="Nomor Pemuatan">{pendingValues.nomor_pemuatan}</Descriptions.Item>
                                <Descriptions.Item label="Driver">{pendingValues.driver || "-"}</Descriptions.Item>
                            </Descriptions>

                            <div style={{ marginTop: 16 }}>
                                <Text strong>Rincian Barang yang Dimuat:</Text>
                                <Table
                                    dataSource={picks}
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    scroll={{ y: 200 }}
                                    style={{ marginTop: 8, border: '1px solid #f0f0f0' }}
                                    columns={[
                                        { title: "Item", dataIndex: "item_code", ellipsis: true },
                                        { title: "Nopen", dataIndex: "nopen", width: 100 },
                                        { title: "Qty", dataIndex: "qty_muat", width: 80, align: "right" },
                                    ]}
                                />
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </Card>
    );
}

const StockPickerRow = ({ record, onAdd }) => {
    const [qty, setQty] = useState(0);
    return (
        <Space>
            <InputNumber
                min={1}
                max={record.jumlah_kemasan}
                value={qty}
                onChange={setQty}
                style={{ width: 80 }}
            />
            <Button
                type="primary"
                size="small"
                disabled={!qty || qty <= 0 || qty > record.jumlah_kemasan}
                onClick={() => onAdd(record, qty)}
            >
                Add
            </Button>
        </Space>
    )
}
